import React, { useState, useEffect } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import { useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout, AmountCard } from '../common/page_component/index.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import { paymentService } from '../../services/ShipmentPaymentService.js';
import userService from '../../services/UserService.js';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import { TrendingUp, Download, DollarSign, CreditCard } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../utils/downloadBlob.js';

const EARNINGS_PAYMENT_MODES = ['CASH', 'UPI'];


export default function DailyEarnings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
    };

    const [fromDate, setFromDate] = useState(getTodayDate());
    const [toDate, setToDate] = useState(getTodayDate());
  const [exporting, setExporting] = useState(false);
  const [statistics, setStatistics] = useState({
    totalEarnings: 0,
    paymentModeBreakdown: {},
  });
  const [paymentModeFilter, setPaymentModeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 20;

  const isAdminRole = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant' || user?.role === 'Hub Admin';
  const isAgent = user?.role === 'Agent';

  useEffect(() => {
    fetchAgents();
    }, []);

    useEffect(() => {
    console.log('Agents state updated:', agents);
}, [agents]);

  useEffect(() => {
    // For agents, auto-select their user_id
    if (isAgent && agents.length > 0 && !userFilter) {
      const currentAgent = agents.find(a => a.user_id === user.user_id);
      if (currentAgent) {
        setUserFilter(currentAgent.user_id);
      }
    }
  }, [isAgent, agents, user]);

  useEffect(() => {
    // Fetch earnings when filters change
    if (userFilter || isAdminRole) {
      fetchEarnings();
    }
  }, [userFilter, fromDate, toDate, paymentModeFilter, currentPage]);

  const fetchAgents = async () => {
    try {
        const response = await userService.getAll(1, 1000, { role_id: 3, is_active: true });
        
        console.log('Agents API Response:', response); // Debug log
        
        // The API returns data in response.data format
        const usersArray = response.data || [];
        
        console.log('Users array:', usersArray); // Debug log
        
        if (isAgent) {
        // If agent, only show their own name
        const currentAgent = usersArray.find(u => u.user_id === user.user_id);
        if (currentAgent) {
            setAgents([currentAgent]);
            setUserFilter(currentAgent.user_id);
        } else {
            setAgents([]);
        }
        } else {
        // If admin/accountant, show all agents
        setAgents(usersArray);
        }
        
        console.log('Agents state set to:', usersArray); // Debug log
    } catch (error) {
        console.error('Error fetching agents:', error);
        if (!isAgent) {
        Alert.error('Failed to fetch agents');
        }
    }
    };

  const earningsPaymentModeParam = () => paymentModeFilter || 'cash,upi';

  const calculateStatistics = (paymentsData) => {
    const stats = {
        totalEarnings: 0,
        paymentModeBreakdown: {
        'CASH': { count: 0, amount: 0 },
        'UPI': { count: 0, amount: 0 },
        },
    };

    paymentsData.forEach(payment => {
        const amount = payment.payment_amount || 0;
        const mode = (payment.payment_mode || '').toUpperCase().trim().replace(' ', '_');

        if (!EARNINGS_PAYMENT_MODES.includes(mode)) return;

        stats.totalEarnings += amount;
        stats.paymentModeBreakdown[mode].count += 1;
        stats.paymentModeBreakdown[mode].amount += amount;
    });

    console.log('Payment Statistics:', stats); // Debug log
    setStatistics(stats);
    };

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        from_date: fromDate,
        to_date: toDate,
        ...(userFilter && { user_id: userFilter }),
        payment_mode: earningsPaymentModeParam(),
      };

      const response = await paymentService.getAllPaymentHistory(params);
      const paymentsData = response.data.data || [];
      let merged = paymentsData;
      setEarnings((prev) => {
        merged = mergeLoadedPage(currentPage, paymentsData, prev);
        return merged;
      });
      setTotalPages(response.data.pagination?.pages || 1);
      calculateStatistics(merged);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      Alert.error('Failed to fetch earnings data');
      setEarnings([]);
      setStatistics({
        totalEarnings: 0,
        paymentModeBreakdown: {},
        lateCharges: { count: 0, amount: 0 },
        labourCharges: { count: 0, amount: 0 },
        deliveryCharges: { count: 0, amount: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        payment_mode: earningsPaymentModeParam(),
        ...(userFilter && { user_id: userFilter })
      });

      const response = await paymentService.exportDailyEarningsExcel(params.toString());
      const filename = `daily_earnings_${fromDate}_to_${toDate}_${new Date().getTime()}.xlsx`;
      await downloadBlob(
        response.data,
        filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
      
      Alert.success('Daily earnings report exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      Alert.error('Failed to export daily earnings report');
    } finally {
      setExporting(false);
    }
  };

    const clearFilters = () => {
    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    setPaymentModeFilter('');
    setCurrentPage(1);
    if (!isAgent) {
        setUserFilter('');
    }
    };

  // Get payment mode display name
  const getPaymentModeDisplay = (mode) => {
    const displayNames = {
      'CASH': 'Cash',
      'UPI': 'UPI',
    };
    return displayNames[mode] || mode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDateRange = () => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    if (fromDate === toDate) {
      return from.toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    }
    
    return `${from.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    })} - ${to.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    })}`;
  };

  const isFiltersActive = () => {
    const today = getTodayDate();
    return (!isAgent && userFilter) || fromDate !== today || toDate !== today || paymentModeFilter;
    };

  return (
    <DashboardLayout>
              <div className="max-w-full mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-6 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-gray-700 flex items-center gap-2 sm:gap-3">
                      <TrendingUp className="w-7 h-7 text-buttonhoverTxt" />
                      Daily Earnings Report
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      View and analyze payment collections
                    </p>
                  </div>
                  <Button variant="secondary" onClick={handleExportExcel} disabled={exporting || earnings.length === 0} loading={exporting} icon={Download}>
                    {exporting ? 'Exporting Excel...' : 'Export Excel Report'}
                  </Button>
                </div>

                <ListFilterPanel hasActiveFilters={isFiltersActive()} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Select Agent {isAgent && <span className="text-xs text-gray-500">(Your Account)</span>}
                    </label>
                    <select
                      value={userFilter}
                      onChange={(e) => { setUserFilter(e.target.value); setCurrentPage(1); }}
                      disabled={isAgent}
                      className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    >
                      <option value="">All Agents</option>
                      {agents && agents.length > 0 ? (
                        agents.map(agent => (
                          <option key={agent.user_id} value={agent.user_id}>
                            {agent.name || agent.username || 'Unknown'} ({agent.city})
                          </option>
                        ))
                      ) : (
                        <option disabled>No agents available</option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                      max={new Date().toISOString().split('T')[0]}
                      min={fromDate}
                      className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Payment Mode</label>
                    <select
                      value={paymentModeFilter}
                      onChange={(e) => { setPaymentModeFilter(e.target.value); setCurrentPage(1); }}
                      className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    >
                      <option value="">All Modes</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <Loader fullScreen={false} size="default" message="Loading earnings data..." />
                ) : (
                  <>
                    {/* Summary Cards - Total Earnings + Payment Mode Breakdown in one row */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-6">

                      {/* Total Earnings */}
                      <AmountCard
                        label="Total Earnings"
                        amount={statistics.totalEarnings}
                        icon={<DollarSign className="w-5 h-5 text-buttonBg" />}
                        color="bg-orange-50"
                      />

                      {EARNINGS_PAYMENT_MODES.map((mode) => {
                        const data = statistics.paymentModeBreakdown[mode] || { count: 0, amount: 0 };
                        return (
                          <AmountCard
                            key={mode}
                            label={`${getPaymentModeDisplay(mode)} · ${data.count} txn`}
                            amount={data.amount}
                            icon={<CreditCard className="w-5 h-5 text-buttonBg" />}
                            color="bg-orange-50"
                          />
                        );
                      })}

                    </div>


                    {/* Earnings Table */}
                    <div className="bg-white rounded-xl shadow-sm">
                      <div className="p-4 border-b">
                        <h3 className="text-lg font-semibold text-secondary">Payment Transactions</h3>
                      </div>
                      
                      {earnings.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="text-6xl mb-4">📊</div>
                          <p className="text-gray-600 text-lg font-semibold">No earnings data found</p>
                          <p className="text-sm text-gray-500 mt-2">
                            {!userFilter && !isAgent
                              ? 'Please select an agent to view earnings'
                              : `No payments recorded for the selected period`}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipment ID</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Balance</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Balance</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recorded By</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {earnings.map((payment) => (
                                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-sm">
                                      {payment.payment_date ? new Date(payment.payment_date).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <Button variant="link" onClick={() => navigate(`/shipments/view/${payment.shipment_id}`)} className="font-mono font-bold text-orange-600 hover:text-orange-700 hover:underline text-sm">
                                        {payment.shipment_id}
                                      </Button>
                                    </td>
                                    <td className="px-4 py-3 font-bold text-green-600 text-sm">
                                      ₹{payment.payment_amount?.toFixed(2) || '0.00'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase">
                                        {payment.payment_mode?.replace(/_/g, ' ') || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      ₹{payment.previous_balance?.toFixed(2) || '0.00'}
                                    </td>
                                    <td className="px-4 py-3 font-bold text-red-600 text-sm">
                                      ₹{payment.new_balance?.toFixed(2) || '0.00'}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {payment.recorded_by_name || 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={payment.remarks}>
                                      {payment.remarks || 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="lg:hidden p-4 space-y-3">
                            {earnings.map((payment) => (
                              <div key={payment.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                                <div className="flex justify-between items-start gap-2 mb-3">
                                  <div className="min-w-0">
                                    <Button variant="link" onClick={() => navigate(`/shipments/view/${payment.shipment_id}`)} className="font-mono font-bold text-orange-600 hover:text-orange-700 hover:underline text-sm p-0">
                                      {payment.shipment_id}
                                    </Button>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {payment.payment_date ? new Date(payment.payment_date).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : 'N/A'}
                                    </p>
                                  </div>
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase whitespace-nowrap shrink-0">
                                    {payment.payment_mode?.replace(/_/g, ' ') || 'N/A'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                  <div>
                                    <span className="text-gray-500">Amount:</span>
                                    <p className="font-bold text-green-600">₹{payment.payment_amount?.toFixed(2) || '0.00'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Prev Balance:</span>
                                    <p className="font-medium text-gray-800">₹{payment.previous_balance?.toFixed(2) || '0.00'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">New Balance:</span>
                                    <p className="font-bold text-red-600">₹{payment.new_balance?.toFixed(2) || '0.00'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Recorded By:</span>
                                    <p className="font-medium text-gray-800 truncate">{payment.recorded_by_name || 'N/A'}</p>
                                  </div>
                                  {payment.remarks && (
                                    <div className="col-span-2">
                                      <span className="text-gray-500">Remarks:</span>
                                      <p className="font-medium text-gray-800">{payment.remarks}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="bg-gray-50 px-5 py-4 flex justify-between items-center border-t-2 border-gray-200 rounded-b-xl">
                        <span className="text-sm text-gray-600">
                          Showing <span className="font-semibold">{earnings.length}</span> payments · Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
                        </span>
                        <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
                      </div>
                    )}
                  </>
                )}
              </div>
    </DashboardLayout>
  );
}